import { knowledgeBasePath, resolveKnowledgeBaseUrl } from '@/config/knowledgeBase';
import { log } from '@/utils/log';
import { computed, ref, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Thin wrapper around the support widget's host API (MILO-422, MILO-417).
 *
 * Everything here goes through `window.MiloSupportChat`, the versioned facade
 * the widget bundle publishes. Reaching into the custom element directly would
 * couple the app to the internal DOM of a separately released component.
 *
 * Two ways into an article, both used by the in-app help panel:
 *
 * - `articleUrl()` + a `data-milo-kb-article` attribute on a real link. The
 *   widget's bridge intercepts the click only when a widget is actually
 *   connected, so the link degrades to the public kennisbank on its own.
 * - `openArticle()` for callers that are not links.
 *
 * `open()` opens the chat itself, used by the error pages to offer "Vraag het
 * Milo" as a way out.
 *
 * `window.MiloSupportChat` is absent whenever the widget bundle never loaded
 * (env flag off, or nobody signed in), so nothing here may assume it exists.
 */
export type UseSupportWidgetReturn = {
    /** Whether a widget instance is connected and can service calls right now. */
    isReady: ComputedRef<boolean>;
    /** Opens the chat panel. Returns false when no widget is available. */
    open: (options?: MiloSupportChatOpenOptions) => boolean;
    /** Public kennisbank URL for a slug, in the user's current language. */
    articleUrl: (slug: string) => string;
    /** Opens the article in the widget panel. Resolves false when unavailable. */
    openArticle: (slug: string, options?: MiloSupportChatOpenOptions) => Promise<boolean>;
};

const knowledgeBaseUrl = resolveKnowledgeBaseUrl(import.meta.env);

/**
 * Mirrors what MiloSupportWidget.vue has on the page. The bridge's own `ready`
 * flag is plain global state that no reactive effect tracks, so a consumer that
 * renders before the widget finishes mounting would never learn about it. The
 * component that mounts the element knows exactly when that happens, so it
 * reports here instead (see setSupportWidgetMounted).
 */
const widgetMounted = ref(false);

/**
 * Called by MiloSupportWidget.vue, the only place that mounts or removes the
 * element. Not part of the public composable API.
 */
export function setSupportWidgetMounted(mounted: boolean): void {
    widgetMounted.value = mounted;
}

export function useSupportWidget(): UseSupportWidgetReturn {
    const { locale } = useI18n();

    // The app-mounted flag is the reactive half; the bridge is consulted as well
    // so a widget mounted by anything else (tests, a different host) still counts.
    const isReady = computed(() => widgetMounted.value || (getApi()?.ready ?? false));

    const open = (options?: MiloSupportChatOpenOptions): boolean => {
        const api = getApi();
        if (!api) return false;

        // Calls made before the element connects are queued by the bridge.
        api.open(options);
        return true;
    };

    const articleUrl = (slug: string): string => `${knowledgeBaseUrl}${knowledgeBasePath(locale.value)}${slug}`;

    const openArticle = async (slug: string, options?: MiloSupportChatOpenOptions): Promise<boolean> => {
        const api = getApi();
        if (!api) return false;

        try {
            await api.openArticle(slug, options);
            return true;
        } catch (error) {
            // An unavailable widget or an unknown slug must never break the page;
            // callers fall back to the public kennisbank link.
            log('Support widget could not open article:', slug, error);
            return false;
        }
    };

    return { isReady, open, articleUrl, openArticle };
}

function getApi(): MiloSupportChatApi | undefined {
    return typeof window === 'undefined' ? undefined : window.MiloSupportChat;
}
